import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "street2", "locality", "region", "postalCode", "country", "countryFlag", "placeId", "lat", "lng"]
  static values = { preserveInput: Boolean }
  static outlets = ["static-map-location"]

  connect() {
    if (this.hasCountryTarget) {
      this.countryTarget.addEventListener('change', this.handleCountryChange.bind(this))
    }

    // Retry initialization every time the field is focused (until it succeeds).
    // The legacy google.maps.places.Autocomplete must bind to a *visible* input
    // to render its dropdown, but in the events wizard the field starts hidden
    // (behind the location-type toggle / inside a closed modal). A persistent
    // focus handler — not a one-shot — is required because earlier focus events
    // can fire while the field is still hidden (step navigation, programmatic
    // reveal) and would otherwise consume a one-time listener.
    this.boundTryInitialize = this.tryInitialize.bind(this)
    this.inputTarget.addEventListener('focus', this.boundTryInitialize)

    this.tryInitialize()
  }

  tryInitialize() {
    if (this.autocomplete) return

    if (!(window.google && window.google.maps && window.google.maps.places)) {
      window.addEventListener('google-maps-loaded', () => this.tryInitialize(), { once: true })
      return
    }

    // Hidden right now — leave it; the focus listener will retry when the field
    // is revealed and focused.
    if (this.isInputHidden()) return

    this.initializeAutocomplete()
  }

  // Walks the ancestor chain looking for `display:none` / `visibility:hidden`.
  // In jsdom (tests) no external CSS is applied, so this reports "visible" and
  // initialization still happens on connect.
  isInputHidden() {
    let el = this.inputTarget
    while (el && el.nodeType === 1) {
      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden') return true
      el = el.parentElement
    }
    return false
  }

  disconnect() {
    if (this.hasCountryTarget) {
      this.countryTarget.removeEventListener('change', this.handleCountryChange.bind(this))
    }
    if (this.boundTryInitialize) {
      this.inputTarget.removeEventListener('focus', this.boundTryInitialize)
    }
  }

  initializeAutocomplete() {
    const input = this.inputTarget

    const initialCountry = this.getSelectedCountry()
    const options = initialCountry ? { componentRestrictions: { country: initialCountry } } : {}
    const autocomplete = new google.maps.places.Autocomplete(input, options)

    this.autocomplete = autocomplete
    this.autocompleteActive = false

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      this.autocompleteActive = false
    })

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()

        setTimeout(() => {
          if (input.value && !this.autocompleteActive) {
          }
        }, 100)
      }
    })

    input.addEventListener('focus', () => {
      this.autocompleteActive = true
    })

    input.addEventListener('blur', () => {
      setTimeout(() => {
        this.autocompleteActive = false
      }, 150)
    })

    const mapDiv = document.createElement('div')
    mapDiv.style.display = 'none'
    document.body.appendChild(mapDiv)
    const map = new google.maps.Map(mapDiv)
    this.placesService = new google.maps.places.PlacesService(map)

    const self = this
    const originalFetch = autocomplete.__proto__.getQueryPredictions
    autocomplete.__proto__.getQueryPredictions = function(request, callback) {
      Promise.all([
        new Promise((resolve) => {
          const searchRequest = { query: request.input, type: 'establishment' }
          const country = self.getSelectedCountry()
          if (country) {
            searchRequest.region = country
          }
          self.placesService.textSearch(searchRequest, (results, status) => {
            resolve(status === google.maps.places.PlacesServiceStatus.OK ? results : [])
          })
        }),
        new Promise((resolve) => {
          originalFetch.call(this, {
            ...request,
            types: ['address']
          }, (results, status) => {
            resolve(status === google.maps.places.PlacesServiceStatus.OK ? results : [])
          })
        })
      ]).then(([establishments, addresses]) => {
        const combined = [...establishments, ...addresses]
        const unique = Array.from(new Map(combined.map(item => [item.place_id, item])).values())
        callback(unique.slice(0, 5), google.maps.places.PlacesServiceStatus.OK)
      })
    }

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      this.handlePlaceSelection(place)
    })
  }

  getSelectedCountry() {
    if (!this.hasCountryTarget) return null

    const selectedCountry = this.countryTarget.value
    return selectedCountry ? selectedCountry.toLowerCase() : null
  }

  handleCountryChange() {
    const country = this.getSelectedCountry()
    if (this.autocomplete) {
      this.autocomplete.setComponentRestrictions(country ? { country } : null)
    }
    this.updateCountryFlag()
  }

  updateCountryFlag() {
    if (!this.hasCountryFlagTarget || !this.hasCountryTarget) return

    const code = this.countryTarget.value
    if (code) {
      const flag = String.fromCodePoint(
        ...code.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
      )
      this.countryFlagTarget.innerHTML = `<span class="text-xl">${flag}</span><span class="text-sm font-normal">${code}</span>`
    }
  }

  handlePlaceSelection(place) {
    if (!place.geometry) {
      console.warn("No details available for input: '" + place.name + "'")
      return
    }

    if (this.hasPlaceIdTarget) {
      this.placeIdTarget.value = place.place_id
    }

    const addressComponents = {}

    place.address_components?.forEach(component => {
      const type = component.types[0]
      addressComponents[type] = component.long_name
      if (type === 'administrative_area_level_1' || type === 'country') {
        addressComponents[type + '_short'] = component.short_name
      }
    })

    if (this.inputTarget && !this.preserveInputValue) {
      const streetNumber = addressComponents.street_number || ''
      const route = addressComponents.route || ''
      this.inputTarget.value = place.name && !route ? place.name : `${streetNumber} ${route}`.trim()
    }

    if (this.hasStreet2Target) {
      const subpremise = addressComponents.subpremise || ''
      this.street2Target.value = subpremise
    }

    if (this.hasLocalityTarget) {
      this.localityTarget.value = addressComponents.locality ||
                                addressComponents.sublocality ||
                                addressComponents.administrative_area_level_2 || ''
    }

    if (this.hasRegionTarget) {
      this.regionTarget.value = addressComponents.administrative_area_level_1_short || ''
      // Filled by script, so nothing fires on its own; forms that derive a
      // field from the state listen for this.
      this.regionTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }

    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = addressComponents.postal_code || ''
    }

    if (this.hasCountryTarget && addressComponents.country_short) {
      this.countryTarget.value = addressComponents.country_short
      this.handleCountryChange()
    }

    if (this.hasLatTarget) {
      this.latTarget.value = place.geometry.location.lat()
    }
    if (this.hasLngTarget) {
      this.lngTarget.value = place.geometry.location.lng()
    }

    if (this.hasStaticMapLocationOutlet) {
      this.staticMapLocationOutlet.updateMarker({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      })
    }
  }

  clear() {
    this.inputTarget.value = ''
    if (this.hasLatTarget) this.latTarget.value = ''
    if (this.hasLngTarget) this.lngTarget.value = ''
    if (this.hasStreet2Target) this.street2Target.value = ''
    if (this.hasLocalityTarget) this.localityTarget.value = ''
    if (this.hasRegionTarget) this.regionTarget.value = ''
    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = ''
    if (this.hasPlaceIdTarget) this.placeIdTarget.value = ''
  }
}
